import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  X, 
  Mail, 
  Phone, 
  Building2, 
  MapPin, 
  Tag, 
  Calendar, 
  Edit,
  Clock,
  FileText,
  PhoneCall,
  CalendarCheck,
  DollarSign,
  ArrowDownRight,
  ArrowUpRight,
  Plus,
  Link2,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarSingle } from '@/components/ui/calendar-single';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTERACTION_TYPES = [
  { value: 'email', label: 'Email', icon: Mail, color: 'bg-blue-500' },
  { value: 'call', label: 'Call', icon: PhoneCall, color: 'bg-green-500' },
  { value: 'meeting', label: 'Meeting', icon: CalendarCheck, color: 'bg-purple-500' },
  { value: 'transaction', label: 'Transaction', icon: DollarSign, color: 'bg-yellow-500' },
  { value: 'note', label: 'Note', icon: FileText, color: 'bg-gray-500' }
];

const ViewContactModal = ({ isOpen, onClose, contact, onEdit }) => {
  const [interactions, setInteractions] = useState([]);
  const [lastContact, setLastContact] = useState(null);
  const [loadingInteractions, setLoadingInteractions] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [activeTab, setActiveTab] = useState('contact'); // 'contact', 'crypto', 'preferences', or 'interactions'
  
  const [logFormData, setLogFormData] = useState({
    type: 'call',
    direction: 'inbound',
    subject: '',
    description: '',
    timestamp: new Date()
  });

  const fetchInteractions = useCallback(async () => {
    if (!contact?.id) return;
    
    try {
      setLoadingInteractions(true);
      const response = await axios.get(
        `${API_URL}/api/contacts/${contact.id}/interactions?limit=10`,
        { withCredentials: true }
      );
      setInteractions(response.data);
    } catch (error) {
      console.error('Failed to fetch interactions:', error);
    } finally {
      setLoadingInteractions(false);
    }
  }, [contact?.id]);

  const fetchLastContact = useCallback(async () => {
    if (!contact?.id) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/contacts/${contact.id}/last-contact`,
        { withCredentials: true }
      );
      setLastContact(response.data);
    } catch (error) {
      console.error('Failed to fetch last contact:', error);
    }
  }, [contact?.id]);

  useEffect(() => {
    if (isOpen && contact) {
      fetchInteractions();
      fetchLastContact();
    }
  }, [isOpen, contact, fetchInteractions, fetchLastContact]);

  const handleLogInteraction = async () => {
    try {
      await axios.post(
        `${API_URL}/api/interactions`,
        {
          contact_id: contact.id,
          ...logFormData,
          timestamp: logFormData.timestamp.toISOString()
        },
        { withCredentials: true }
      );
      
      setShowLogModal(false);
      setLogFormData({
        type: 'call',
        direction: 'inbound',
        subject: '',
        description: '',
        timestamp: new Date()
      });
      
      // Refresh interactions
      fetchInteractions();
      fetchLastContact();
    } catch (error) {
      console.error('Failed to log interaction:', error);
    }
  };

  if (!isOpen || !contact) return null;

  const getStatusStyle = (status) => {
    const styles = {
      active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      inactive: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
    };
    return styles[status] || styles.inactive;
  };

  const getTypeStyle = (type) => {
    const styles = {
      investor: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      portfolio: 'bg-purple-500/10 text-purple-400 border-purple-500/20'
    };
    return styles[type] || styles.investor;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not set';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return null;
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const getTypeIcon = (type) => {
    const typeConfig = INTERACTION_TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.icon : FileText;
  };

  const getTypeColor = (type) => {
    const typeConfig = INTERACTION_TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.color : 'bg-gray-500';
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0f1115] border border-white/10 rounded-lg shadow-2xl"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0f1115] border-b border-white/10 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center text-lg font-medium text-zinc-200">
                  {contact.firstName?.[0]}{contact.lastName?.[0]}
                </div>
                
                {/* Name and Title */}
                <div>
                  <h2 className="text-2xl font-semibold text-zinc-100">
                    {contact.firstName} {contact.lastName}
                  </h2>
                  {contact.role && contact.company && (
                    <p className="text-sm text-zinc-400 mt-1">
                      {contact.role} at {contact.company}
                    </p>
                  )}
                  {/* Status and Type Badges */}
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={getStatusStyle(contact.status)}>
                      {contact.status?.charAt(0).toUpperCase() + contact.status?.slice(1)}
                    </Badge>
                    <Badge className={getTypeStyle(contact.type)}>
                      {contact.type?.charAt(0).toUpperCase() + contact.type?.slice(1)}
                    </Badge>
                    {lastContact?.last_contact && (
                      <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                        <Clock className="w-3 h-3 mr-1" />
                        Last contact {getTimeAgo(lastContact.last_contact)}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={onClose}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 mt-6 -mb-6">
              <button
                onClick={() => setActiveTab('contact')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === 'contact'
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-zinc-400 hover:text-zinc-300'
                }`}
              >
                Personal Info
              </button>
              <button
                onClick={() => setActiveTab('crypto')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === 'crypto'
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-zinc-400 hover:text-zinc-300'
                }`}
              >
                Crypto & Investment Profile
              </button>
              <button
                onClick={() => setActiveTab('preferences')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === 'preferences'
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-zinc-400 hover:text-zinc-300'
                }`}
              >
                Preferences
              </button>
              <button
                onClick={() => setActiveTab('interactions')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === 'interactions'
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-zinc-400 hover:text-zinc-300'
                }`}
              >
                Interactions {interactions.length > 0 && `(${interactions.length})`}
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'contact' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                {/* First Name */}
                {contact.firstName && (
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 mt-0.5 text-zinc-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        First Name
                      </span>
                      <span className="text-zinc-200">{contact.firstName}</span>
                    </div>
                  </div>
                )}

                {/* Last Name */}
                {contact.lastName && (
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 mt-0.5 text-zinc-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        Last Name
                      </span>
                      <span className="text-zinc-200">{contact.lastName}</span>
                    </div>
                  </div>
                )}

                {/* Email */}
                {contact.email && (
                  <div className="flex items-start gap-3">
                    <Mail size={20} className="text-zinc-500 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        Email
                      </span>
                      <a 
                        href={`mailto:${contact.email}`}
                        className="text-zinc-200 hover:text-blue-400 transition-colors"
                      >
                        {contact.email}
                      </a>
                    </div>
                  </div>
                )}

                {/* Phone */}
                {contact.phone && (
                  <div className="flex items-start gap-3">
                    <Phone size={20} className="text-zinc-500 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        Phone
                      </span>
                      <span className="text-zinc-200">{contact.phone}</span>
                    </div>
                  </div>
                )}

                {/* Company */}
                {contact.company && (
                  <div className="flex items-start gap-3">
                    <Building2 size={20} className="text-zinc-500 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        Company
                      </span>
                      <span className="text-zinc-200">{contact.company}</span>
                    </div>
                  </div>
                )}

                {/* Role */}
                {contact.role && (
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 mt-0.5 text-zinc-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        Role
                      </span>
                      <span className="text-zinc-200">{contact.role}</span>
                    </div>
                  </div>
                )}

                {/* Status */}
                {contact.status && (
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 mt-0.5 text-zinc-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        Status
                      </span>
                      <Badge className={getStatusStyle(contact.status)}>
                        {contact.status?.charAt(0).toUpperCase() + contact.status?.slice(1)}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Type */}
                {contact.type && (
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 mt-0.5 text-zinc-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        Type
                      </span>
                      <Badge className={getTypeStyle(contact.type)}>
                        {contact.type?.charAt(0).toUpperCase() + contact.type?.slice(1)}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Location */}
                {contact.location && (
                  <div className="flex items-start gap-3">
                    <MapPin size={20} className="text-zinc-500 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        Location
                      </span>
                      <span className="text-zinc-200">{contact.location}</span>
                    </div>
                  </div>
                )}

                {/* Deal Size */}
                {contact.dealSize && (
                  <div className="flex items-start gap-3">
                    <DollarSign size={20} className="text-zinc-500 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        Deal Size
                      </span>
                      <span className="text-zinc-200">{formatCurrency(contact.dealSize)}</span>
                    </div>
                  </div>
                )}

                {/* Last Contact */}
                {lastContact?.last_contact && (
                  <div className="flex items-start gap-3">
                    <Calendar size={20} className="text-zinc-500 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                        Last Contact
                      </span>
                      <span className="text-zinc-200">{formatDate(lastContact.last_contact)}</span>
                    </div>
                  </div>
                )}

                {/* Tags */}
                {contact.tags && contact.tags.length > 0 && (
                  <div className="flex items-start gap-3 md:col-span-2">
                    <Tag size={20} className="text-zinc-500 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-2">
                        Tags
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {contact.tags.map((tag) => (
                          <span
                            key={tag.id}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm border"
                            style={{
                              backgroundColor: `${tag.color}15`,
                              color: tag.color,
                              borderColor: `${tag.color}30`
                            }}
                          >
                            <Tag size={12} />
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'crypto' && (
              <div className="space-y-6">
                {/* Crypto & Investment Profile */}
                {(contact.connectionType || contact.notes || contact.socialLinkedin || contact.socialTwitter || 
                  (contact.investmentFocus && contact.investmentFocus.length > 0) ||
                  (contact.walletAddresses && contact.walletAddresses.length > 0) ||
                  (contact.cryptoHoldings && Object.keys(contact.cryptoHoldings).length > 0)) ? (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Crypto & Investment Profile
                    </h3>

                    {/* Connection Type & Social */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {contact.connectionType && (
                        <div>
                          <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                            Connection Type
                          </span>
                          <Badge variant={contact.connectionType === 'internal' ? 'default' : 'secondary'}>
                            {contact.connectionType}
                          </Badge>
                        </div>
                      )}
                      {contact.socialLinkedin && (
                        <div>
                          <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                            LinkedIn
                          </span>
                          <a
                            href={contact.socialLinkedin.startsWith('http') ? contact.socialLinkedin : `https://${contact.socialLinkedin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                          >
                            <Link2 size={12} />
                            Profile
                          </a>
                        </div>
                      )}
                      {contact.socialTwitter && (
                        <div>
                          <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-1">
                            Twitter/X
                          </span>
                          <a
                            href={`https://twitter.com/${contact.socialTwitter.replace('@', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                          >
                            <Link2 size={12} />
                            {contact.socialTwitter}
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {contact.notes && (
                      <div>
                        <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-2">
                          Notes
                        </span>
                        <p className="text-zinc-300 text-sm whitespace-pre-wrap bg-[#0a0a0a] p-3 rounded border border-white/10">
                          {contact.notes}
                        </p>
                      </div>
                    )}

                    {/* Investment Focus */}
                    {contact.investmentFocus && contact.investmentFocus.length > 0 && (
                      <div>
                        <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-2">
                          Investment Focus
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {contact.investmentFocus.map((focus, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {focus}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Wallet Addresses */}
                    {contact.walletAddresses && contact.walletAddresses.length > 0 && (
                      <div>
                        <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-2 flex items-center gap-2">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          Wallet Addresses
                        </span>
                        <div className="space-y-2">
                          {contact.walletAddresses.map((wallet, idx) => (
                            <div key={idx} className="bg-[#0a0a0a] p-3 rounded border border-white/10">
                              <div className="flex items-center justify-between mb-1">
                                <Badge variant="secondary" className="text-xs">{wallet.type}</Badge>
                                {wallet.label && (
                                  <span className="text-xs text-zinc-500">{wallet.label}</span>
                                )}
                              </div>
                              <code className="text-xs text-zinc-400 font-mono break-all">
                                {wallet.address}
                              </code>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Crypto Holdings */}
                    {contact.cryptoHoldings && Object.keys(contact.cryptoHoldings).length > 0 && (
                      <div>
                        <span className="text-xs text-zinc-500 uppercase tracking-wider block mb-2 flex items-center gap-2">
                          <TrendingUp size={12} />
                          Crypto Holdings
                        </span>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(contact.cryptoHoldings).map(([symbol, holding]) => {
                            // Ensure holding is an object with the expected structure
                            if (!holding || typeof holding !== 'object') return null;
                            
                            return (
                              <div key={symbol} className="bg-[#0a0a0a] p-3 rounded border border-white/10">
                                <div className="flex items-center justify-between mb-2">
                                  <Badge variant="default" className="font-mono">{symbol}</Badge>
                                  <span className="text-zinc-300 font-semibold">{holding.amount || '0'}</span>
                                </div>
                                <div className="space-y-1 text-xs text-zinc-500">
                                  {holding.costBasis && (
                                    <div className="flex justify-between">
                                      <span>Cost Basis:</span>
                                      <span className="text-zinc-400">${holding.costBasis}</span>
                                    </div>
                                  )}
                                  {holding.acquisitionDate && (
                                    <div className="flex justify-between">
                                      <span>Acquired:</span>
                                      <span className="text-zinc-400">{new Date(holding.acquisitionDate).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="mx-auto w-16 h-16 rounded-full bg-zinc-800/50 flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <p className="text-zinc-500 mb-2">No crypto or investment information</p>
                    <p className="text-sm text-zinc-600">
                      Add investment focus, wallet addresses, or crypto holdings to this contact
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <div className="space-y-4">
                  {/* Notes Section */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FileText size={20} className="text-zinc-500" />
                      <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">
                        Notes
                      </h3>
                    </div>
                    {contact.preferencesNotes ? (
                      <div className="bg-[#0a0a0a] p-4 rounded-lg border border-white/10">
                        <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed">
                          {contact.preferencesNotes}
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-[#0a0a0a] rounded-lg border border-white/10 border-dashed">
                        <FileText className="mx-auto h-12 w-12 text-zinc-600 mb-3" />
                        <p className="text-zinc-500 mb-2">No notes added yet</p>
                        <p className="text-sm text-zinc-600">
                          Click "Edit" to add notes and preferences for this contact
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'interactions' && (
              <div className="space-y-4">
                {/* Quick Action Buttons */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">
                    Interaction Timeline
                  </h3>
                  <Button
                    onClick={() => setShowLogModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Log Interaction
                  </Button>
                </div>

                {/* Interactions List */}
                {loadingInteractions ? (
                  <div className="text-center py-8 text-zinc-500">Loading interactions...</div>
                ) : interactions.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="mx-auto h-12 w-12 text-zinc-600 mb-4" />
                    <p className="text-zinc-500 mb-2">No interactions logged yet</p>
                    <p className="text-sm text-zinc-600">
                      Start by logging your first interaction with this contact
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {interactions.map((interaction) => {
                      const TypeIcon = getTypeIcon(interaction.type);
                      const DirectionIcon = interaction.direction === 'inbound' ? ArrowDownRight : ArrowUpRight;

                      return (
                        <div
                          key={interaction.id}
                          className="bg-[#111318] border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`${getTypeColor(interaction.type)} p-2 rounded-lg`}>
                              <TypeIcon className="h-4 w-4 text-white" />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-white font-medium text-sm">
                                    {interaction.subject || `${interaction.type.charAt(0).toUpperCase() + interaction.type.slice(1)} Interaction`}
                                  </h4>
                                  <span className="flex items-center gap-1 text-xs text-zinc-400">
                                    <DirectionIcon className="h-3 w-3" />
                                    {interaction.direction}
                                  </span>
                                </div>
                                <span className="text-xs text-zinc-500 flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatTimestamp(interaction.timestamp)}
                                </span>
                              </div>

                              {interaction.description && (
                                <p className="text-zinc-400 text-sm line-clamp-2 mb-2">
                                  {interaction.description}
                                </p>
                              )}

                              <div className="flex items-center gap-3 text-xs text-zinc-500">
                                {interaction.channel && <span>via {interaction.channel}</span>}
                                <span>by {interaction.created_by_name}</span>
                                {interaction.source !== 'manual' && (
                                  <span className="text-blue-400">• Auto-logged</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-[#0f1115] border-t border-white/10 p-6 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={onClose}
              className="bg-transparent border-white/10 text-zinc-300 hover:bg-white/5"
            >
              Close
            </Button>
            {(activeTab === 'contact' || activeTab === 'crypto' || activeTab === 'preferences') && (
              <Button
                onClick={() => {
                  onClose();
                  onEdit(contact, activeTab);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Edit size={16} className="mr-2" />
                Edit
              </Button>
            )}
          </div>
        </motion.div>
      </div>

      {/* Log Interaction Modal */}
      <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
        <DialogContent className="bg-[#0f1115] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Log New Interaction</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Type & Direction */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Type</label>
                <Select value={logFormData.type} onValueChange={(value) => setLogFormData({ ...logFormData, type: value })}>
                  <SelectTrigger className="bg-[#111318] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERACTION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-2 block">Direction</label>
                <Select value={logFormData.direction} onValueChange={(value) => setLogFormData({ ...logFormData, direction: value })}>
                  <SelectTrigger className="bg-[#111318] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inbound">Inbound</SelectItem>
                    <SelectItem value="outbound">Outbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Subject/Title</label>
              <Input
                placeholder="Brief title of the interaction"
                value={logFormData.subject}
                onChange={(e) => setLogFormData({ ...logFormData, subject: e.target.value })}
                className="bg-[#111318] border-white/10"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Notes/Description</label>
              <Textarea
                placeholder="Add details about this interaction..."
                value={logFormData.description}
                onChange={(e) => setLogFormData({ ...logFormData, description: e.target.value })}
                className="bg-[#111318] border-white/10 min-h-[100px]"
              />
            </div>

            {/* Timestamp */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Date & Time</label>
              <CalendarSingle
                date={logFormData.timestamp}
                onDateChange={(date) => setLogFormData({ ...logFormData, timestamp: date })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLogModal(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleLogInteraction}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Log Interaction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AnimatePresence>
  );
};

export default ViewContactModal;
